/**
 * Scene sprites (R32) — the D96 contract's first authored half: an asset, an
 * anchor, and a `visibleWhen`, bound to a room and composited over its plate.
 *
 * @remarks
 * D96 ratified sprites and the scene layer as ONE contract: a scene node is
 * `depicts` + `visibleWhen` (which is `conditionSchema` VERBATIM, so the
 * evaluator, the semantic walk, and the constant-condition validator work on
 * day one) + a renderer-local `placement`. The 2D compositor is the
 * contract's first renderer; a panorama or a Unity-class engine later reads
 * the same declarations with its own placement kind — which is why
 * `placement` is kind-discriminated rather than one shape pretending to be
 * general.
 *
 * The `compositor2d` placement is NUMERIC where `framePosition` (R31) is an
 * enum, and both are right: a doorway's band is a reading convention a
 * painter judges, but a composited sprite needs exact registration — and the
 * D110 silhouette rule ("a variant pair shares one silhouette") is what
 * makes one placement serve a sprite's every lighting variant, so a lighting
 * change is a change of light and never of position. Fractions are of the
 * PLATE's frame (0..1 from the top-left), so the declaration survives any
 * rendered size.
 *
 * Sprites carry no `alt`: the plate's caption describes the frame once (D89
 * rule 6), the prose already names the objects, and a screen-reader user
 * must not hear the room three times — the compositor renders sprites as
 * decorative layers, invisible to the accessibility tree.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { actorIdSchema, itemIdSchema, spriteIdSchema } from './identifiers.js';
import { isVideoAssetPath } from './images.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * What a sprite may depict (D236): a THING, or a BODY — an item id or an
 * actor id.
 *
 * @remarks
 * Content ids are one space (D232: an actor may not share its id with an
 * item or a room), so one id names one entity and the loader resolves which
 * kind it is — the same move an id-shaped topic key makes (D231). Two fields
 * would have let a sprite declare both and depict two things at once; one
 * field cannot. The union is the honest type: a consumer that needs the kind
 * asks the pack, exactly as the loader does.
 *
 * A body is depicted rather than painted for the reason §1a of the
 * PATHOLOGY assets gives for anything that comes and goes: an actor moves,
 * falls, and leaves, and a plate cannot. What the promise asks of a body is
 * the loader's (`sprite-depiction.ts`): the sprite shows only while the body
 * is present where it is drawn.
 */
export const depictedEntityIdSchema = z.union([itemIdSchema, actorIdSchema], {
	error: 'Invalid depicts: an item id or an actor id — content ids are snake_case and letter-first (e.g. "actor_drain_animal")',
});

/** An item or an actor a sprite depicts — see {@link depictedEntityIdSchema}. */
export type DepictedEntityId = z.infer<typeof depictedEntityIdSchema>;

/** A fraction of the plate frame, 0..1 — resolution-independent registration. */
const frameFractionSchema = z.number().min(0).max(1);

/**
 * The 2D compositor's placement: the sprite's top-left corner and width as
 * fractions of the plate frame (height follows the asset's own aspect).
 */
export const compositor2dPlacementSchema = z
	.strictObject({
		kind: z.literal('compositor2d'),
		width: frameFractionSchema,
		x: frameFractionSchema,
		y: frameFractionSchema,
	})
	.readonly();

/** The 2D compositor's placement — see {@link compositor2dPlacementSchema}. */
export type Compositor2dPlacement = z.infer<typeof compositor2dPlacementSchema>;

/**
 * Where a renderer puts the sprite — kind-discriminated so the panorama and
 * Unity rungs add their own shapes without touching this one (D96's
 * renderer-local ruling). One kind today; the discriminant is the contract.
 */
export const spritePlacementSchema = z.discriminatedUnion('kind', [compositor2dPlacementSchema]);

/** A sprite's renderer-local placement — see {@link spritePlacementSchema}. */
export type SpritePlacement = z.infer<typeof spritePlacementSchema>;

/**
 * How a sprite's pixels land on the plate (D146, Calder's §3.8: a sprite's
 * value is only right relative to the plate it lands on, and that is judged
 * composited — so it is tuned in data, never re-rendered into the asset).
 *
 * @remarks
 * `blend: 'screen'` exists because a projector cannot project black: an
 * emitted-light sprite — a projected frame, a glow, a display, a fire —
 * alpha-blended at any opacity paints its blacks onto the plate as darkness;
 * screen lets only the light land. `opacity` is the value knob, and zero is
 * refused: a sprite nobody can see is dead content, and `visibleWhen` is
 * where a sprite goes away.
 */
export const spriteCompositingSchema = z
	.strictObject({
		blend: z.enum(['normal', 'screen']).default('normal'),
		opacity: z.number().gt(0).max(1).default(1),
	})
	.readonly();

/** A sprite's compositing — see {@link spriteCompositingSchema}. */
export type SpriteCompositing = z.infer<typeof spriteCompositingSchema>;

/** The compositing a sprite gets when it declares none: paint, at full value. */
export const DEFAULT_SPRITE_COMPOSITING: SpriteCompositing = { blend: 'normal', opacity: 1 };

/**
 * D255: the movement INTO a sprite — a play-once file drawn at the sprite's
 * placement on the scene change where the sprite takes the place of
 * `fromSpriteId`, ending on the sprite's own first frame so the hand-off to
 * `src` is invisible.
 *
 * @remarks
 * A transition is an edge between two of a thing's states, not a property of
 * one state, which is why `fromSpriteId` is required: TALLY's white lamp is
 * reached by waking it free (dark to white — the wake plays) and by releasing
 * it awake (amber to white — nothing to play), and only the sprite it
 * replaces tells the two apart. It plays only where the player watches the
 * change: a scene that opens with the sprite already true — a reload, a
 * revisit — shows the sprite at rest. A viewer who asked for reduced motion
 * never sees it. Which of those holds is the renderer's to know, so this
 * declares the edge and the renderer decides (as `reducedMotionSrc`).
 */
export const spriteTransitionSchema = z
	.strictObject({
		fromSpriteId: spriteIdSchema,
		src: nonBlankStringSchema,
	})
	.readonly();

/** A sprite's play-once arrival — see {@link spriteTransitionSchema}. */
export type SpriteTransition = z.infer<typeof spriteTransitionSchema>;

/**
 * One scene sprite: an asset composited over a room's plate while its
 * condition holds.
 *
 * @remarks
 * `depicts` is optional — a light pool or drifting dust depicts nothing —
 * but when present it is the D89 promise (the thing referable wherever the
 * sprite shows; D236: the body present wherever it is drawn) and the key of
 * the shared-placement rule: two sprites depicting the same item or actor
 * in the same room are variants of one thing — a lighting change, or a body
 * alive and fallen — and the D110 silhouette rule makes their placements
 * identical by construction — the validator holds them to it, so a fall is
 * a change of pose and never of place. `visibleWhen` is required:
 * an unconditional sprite is the plate's own paint, and belongs in the
 * plate.
 *
 * `reducedMotionSrc` (D182) is the still a moving sprite owes
 * (content-standards §3): a viewer who has asked their platform for reduced
 * motion is shown this file in place of `src`, at the same placement and
 * compositing — a frame that says the same thing, not a different picture.
 * Optional because a sprite that does not move owes nothing; a sprite
 * that does and declares none plays for every viewer, which is the defect
 * Calder found on 2026-09-14 and the reason the field exists.
 *
 * D266: no file of a sprite is a video. A sprite composites by fraction over
 * its plate as a picture, and its motion is an animated WebP's; a video is
 * only ever a whole plate.
 */
export const spriteDefinitionSchema = z
	.strictObject({
		compositing: spriteCompositingSchema.default(DEFAULT_SPRITE_COMPOSITING),
		depicts: depictedEntityIdSchema.optional(),
		placement: spritePlacementSchema,
		reducedMotionSrc: nonBlankStringSchema.optional(),
		spriteId: spriteIdSchema,
		src: nonBlankStringSchema,
		/** D255: the play-once file that brings this sprite in from another — see {@link spriteTransitionSchema}. */
		transition: spriteTransitionSchema.optional(),
		visibleWhen: conditionSchema,
	})
	.readonly()
	.refine((sprite) => ![sprite.src, sprite.reducedMotionSrc, sprite.transition?.src].some((path) => path !== undefined && isVideoAssetPath(path)), {
		message: 'a sprite names a video — a sprite composites over its plate as a picture, and only a whole plate may be a video (D266)',
		path: ['src'],
	});

/** One scene sprite — see {@link spriteDefinitionSchema}. */
export type SpriteDefinition = z.infer<typeof spriteDefinitionSchema>;
